import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "@/services/api";
import { classNames } from "@/utils";
import DataRoomTab from "./dataroom";
import InformationsTab from "./informations";
import NoteTab from "./note";
import TodoTab from "./todo";
import EditorCctpTab from "./editor/editor_cctp";
import BudgetTab from "./budget";
import Claude from "./claude";
import KnowledgeTab from "./knowledge";

const ViewAnswer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [answer, setAnswer] = useState({
    title: "",
    description: "",
    status: "pending",
    date: "",
    montant: 0,
    entreprise: "",
    note: 0,
    user_id: "",
    user_name: "",
    user_avatar: "",
    notes: [],
    url: "",
  });

  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("informations");
  const [showUrlModal, setShowUrlModal] = useState(false);

  useEffect(() => {
    loadAnswer();
  }, []);

  async function loadAnswer() {
    try {
      setLoading(true);
      const { ok, data } = await api.get(`/mission_ao_answer/${id}`);
      if (!ok) throw new Error("Failed to fetch answer");
      setAnswer(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Error loading answer");
    }
  }

  const handleAddUrl = async (newUrl) => {
    try {
      const { ok } = await api.put(`/mission_ao_answer/${id}`, { url: newUrl });
      if (!ok) throw new Error("Failed to update URL");
      setAnswer({ ...answer, url: newUrl });
      setShowUrlModal(false);
      toast.success("URL added successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error updating URL");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );

  const statusBadge = (status) => {
    const statusMap = {
      pending: { bg: "bg-yellow-100", text: "text-yellow-800", label: "En attente" },
      need_info: { bg: "bg-blue-100", text: "text-blue-800", label: "Besoin d'info" },
      approved: { bg: "bg-green-100", text: "text-green-800", label: "Approuvé" },
      rejected: { bg: "bg-red-100", text: "text-red-800", label: "Rejeté" },
      "waiting result": { bg: "bg-purple-100", text: "text-purple-800", label: "En attente de résultat" },
    };

    const { bg, text, label } = statusMap[status] || statusMap.pending;

    return <span className={`${bg} ${text} px-3 py-1 rounded-full text-sm font-medium`}>{label}</span>;
  };

  return (
    <div className="mx-auto">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-gray-900">{answer.title || "Sans titre"}</h1>
              {statusBadge(answer.status)}
            </div>
            <div className="flex items-center gap-3">
              {answer.montant > 0 && (
                <div className="text-xl font-bold text-green-600">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(answer.montant)}</div>
              )}
              {answer.user_avatar && (
                <div className="flex items-center gap-2">
                  <img src={answer.user_avatar} alt={answer.user_name} className="w-8 h-8 rounded-full" />
                  <span className="text-sm text-gray-700">{answer.user_name}</span>
                </div>
              )}
              <button onClick={() => navigate("/rfp")} className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded-md transition-all">
                Retour
              </button>
            </div>
          </div>
        </div>

        <nav className="border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between p-2">
            <div className="flex gap-2">
              <TabItem title="Informations" tab="informations" setTab={setTab} active={tab === "informations"} />
              <TabItem title="Data Room" tab="data-room" setTab={setTab} active={tab === "data-room"} />
              <TabItem title="Editor CCTP" tab="editor-cctp" setTab={setTab} active={tab === "editor-cctp"} />
              <TabItem title="Notation" tab="note" setTab={setTab} active={tab === "note"} />
              <TabItem title="Todo" tab="todo" setTab={setTab} active={tab === "todo"} />
              <TabItem title="Notes & Budget" tab="budget" setTab={setTab} active={tab === "budget"} />
              <TabItem title="Connaissances" tab="knowledge" setTab={setTab} active={tab === "knowledge"} />
              <TabItem title="Claude" tab="claude" setTab={setTab} active={tab === "claude"} />
              <TabItem title="Raw" tab="raw" setTab={setTab} active={tab === "raw"} />
              {answer.url && (
                <button
                  onClick={() => window.open(answer.url, "_blank")}
                  className={classNames("px-4 py-2 text-sm font-medium rounded-lg transition-all", "text-blue-600 hover:text-blue-800 hover:bg-blue-50")}>
                  Mémoire Technique
                </button>
              )}
              <button
                onClick={() => setShowUrlModal(true)}
                className={classNames("px-3 py-1 text-sm font-medium rounded-lg transition-all", "text-gray-600 hover:text-blue-600 hover:bg-white/60")}>
                ⋮
              </button>
            </div>
          </div>
        </nav>

        {showUrlModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <h3 className="text-lg font-medium mb-4">{answer.url ? "Modifier l'URL" : "Ajouter une URL"}</h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddUrl(e.target.url.value);
                }}>
                <input name="url" type="url" defaultValue={answer.url} className="w-full px-3 py-2 border rounded-lg mb-4" placeholder="Entrer l'URL" required />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowUrlModal(false)} className="px-4 py-2 text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-100">
                    Annuler
                  </button>
                  <button type="submit" className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700">
                    Enregistrer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div>
          {tab === "informations" && <InformationsTab answer={answer} setAnswer={setAnswer} />}
          {tab === "data-room" && <DataRoomTab answer={answer} setAnswer={setAnswer} />}
          {tab === "editor-cctp" && <EditorCctpTab answer={answer} setAnswer={setAnswer} />}
          {tab === "note" && <NoteTab answer={answer} setAnswer={setAnswer} />}
          {tab === "todo" && <TodoTab answer={answer} setAnswer={setAnswer} />}
          {tab === "knowledge" && <KnowledgeTab answer={answer} setAnswer={setAnswer} />}
          {tab === "claude" && <Claude answer={answer} setAnswer={setAnswer} />}
          {tab === "budget" && <BudgetTab answer={answer} setAnswer={setAnswer} />}
          {tab === "raw" && (
            <div className="bg-gray-50 rounded-lg p-6">
              <pre className="text-sm overflow-auto">{JSON.stringify(answer, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TabItem = ({ tab, title, setTab, active }) => (
  <button
    onClick={() => setTab(tab)}
    className={classNames(
      "px-4 py-2 text-sm font-medium rounded-lg transition-all",
      active ? "text-blue-600 bg-blue-50 shadow-sm" : "text-gray-600 hover:text-blue-600 hover:bg-blue-50/60",
    )}>
    {title}
  </button>
);

export default ViewAnswer;
